// Ported from files/snapdraw-design-kit/code/src/lib/i18n/strings/reports.ts.
// Backlog H4 v2 (2026-08-24): the page was rebuilt around a receivables-
// first structure ("How much am I owed? Who owes it? Am I trending
// better?") after the original 4-tile/chart+aging/Top-Clients layout and
// its dead "Last 6 Months" chip were retired — see @/lib/docStatus and
// @/lib/clients (computeClientReceivables) for the real data behind every
// key below. All copy here is real (no "not tracked yet" placeholders
// left): due dates (J1) and the real cash timeline (J3) already shipped
// what this needed.
export const en = {
  "reports.eyebrow": "Money Owed",
  "reports.title": "Reports",
  "reports.exportCsv": "Export CSV",
  "reports.owed.label": "Outstanding right now",
  "reports.owed.overdue": "{amount} of that is past due — tap to chase it",
  "reports.owed.allCurrent": "Nothing past due. Everything outstanding is still within terms.",
  "reports.owed.empty": "Send your first pay application and your open balance shows up here.",
  "reports.owed.note":
    "Open balance is a snapshot of today — sent pay applications not yet closed out. It is not a six-month total, so it will not equal invoiced minus collected.",
  "reports.summary.invoiced": "Invoiced",
  "reports.summary.invoiced.note": "Pay apps sent · last 6 months",
  "reports.summary.collected": "Collected",
  "reports.summary.collected.note": "Cash received · last 6 months",
  "reports.whoOwes": "Who Owes You",
  "reports.asOfToday": "As of today",
  "reports.loadingAging": "Loading aging buckets",
  "reports.agingEmpty.title": "Nothing outstanding",
  "reports.agingEmpty.body": "When pay applications go past due they get bucketed here by age.",
  "reports.aging.bucket.current": "Current",
  "reports.aging.bucket.days1to30": "1–30 Days Late",
  "reports.aging.bucket.days31to60": "31–60 Days Late",
  "reports.aging.bucket.days60plus": "60+ Days Late",
  "reports.aging.docCount": "{count} documents",
  "reports.aging.docCount.one": "{count} document",
  "reports.callList": "Collections Call List",
  "reports.callList.note": "Sorted by how late they are, then by how much they owe.",
  "reports.loadingCallList": "Loading call list",
  "reports.callList.late": "{days} days late · {bucket}",
  "reports.callList.current": "Within terms",
  "reports.callList.overdueAmount": "{amount} late",
  "reports.callList.empty.title": "No one owes you",
  "reports.callList.empty.body": "Every pay application you have sent is closed out.",
  "reports.invoicedVsCollected": "Invoiced vs Collected",
  "reports.chart.period": "{range}",
  "reports.chart.gap": "You billed {amount} more than you collected over these six months — that gap is what is still on the street.",
  "reports.loadingChart": "Loading revenue chart",
  "reports.revenueEmpty.title": "No revenue history",
  "reports.revenueEmpty.body": "Send and collect on your first invoices and the six-month trend fills in here.",
  "reports.legend.invoiced": "Invoiced",
  "reports.legend.collected": "Collected",
  "reports.bidding": "Bidding",
  "reports.summary.winRate": "Win Rate",
  "reports.summary.winRate.note": "All quotes to date, accepted or closed",
  "reports.definitions":
    "Invoiced counts G702/G703 pay applications and Invoices once sent — drafts and unbilled quotes are excluded. Quotes count toward win rate only.",
} as const;

export const es: Record<keyof typeof en, string> = {
  "reports.eyebrow": "Dinero por Cobrar",
  "reports.title": "Reportes",
  "reports.exportCsv": "Exportar CSV",
  "reports.owed.label": "Pendiente ahora mismo",
  "reports.owed.overdue": "{amount} ya está vencido — toca para cobrarlo",
  "reports.owed.allCurrent": "Nada vencido. Todo lo pendiente sigue dentro del plazo.",
  "reports.owed.empty": "Envía tu primera aplicación de pago y tu saldo aparecerá aquí.",
  "reports.owed.note":
    "El saldo pendiente es una foto de hoy — aplicaciones de pago enviadas y aún sin cerrar. No es un total de seis meses, así que no será igual a facturado menos cobrado.",
  "reports.summary.invoiced": "Facturado",
  "reports.summary.invoiced.note": "Aplicaciones de pago enviadas · últimos 6 meses",
  "reports.summary.collected": "Cobrado",
  "reports.summary.collected.note": "Efectivo recibido · últimos 6 meses",
  "reports.whoOwes": "Quién Te Debe",
  "reports.asOfToday": "Al día de hoy",
  "reports.loadingAging": "Cargando antigüedad de cartera",
  "reports.agingEmpty.title": "No hay nada pendiente",
  "reports.agingEmpty.body": "Cuando las aplicaciones de pago se vencen se agrupan aquí por antigüedad.",
  "reports.aging.bucket.current": "Al día",
  "reports.aging.bucket.days1to30": "1–30 días tarde",
  "reports.aging.bucket.days31to60": "31–60 días tarde",
  "reports.aging.bucket.days60plus": "60+ días tarde",
  "reports.aging.docCount": "{count} documentos",
  "reports.aging.docCount.one": "{count} documento",
  "reports.callList": "Lista de Cobranza",
  "reports.callList.note": "Ordenada por qué tan tarde están y luego por cuánto deben.",
  "reports.loadingCallList": "Cargando lista de cobranza",
  "reports.callList.late": "{days} días tarde · {bucket}",
  "reports.callList.current": "Dentro del plazo",
  "reports.callList.overdueAmount": "{amount} vencido",
  "reports.callList.empty.title": "Nadie te debe",
  "reports.callList.empty.body": "Todas las aplicaciones de pago que enviaste están cerradas.",
  "reports.invoicedVsCollected": "Facturado vs Cobrado",
  "reports.chart.period": "{range}",
  "reports.chart.gap": "Facturaste {amount} más de lo que cobraste en estos seis meses — esa diferencia es lo que sigue en la calle.",
  "reports.loadingChart": "Cargando gráfico de ingresos",
  "reports.revenueEmpty.title": "Sin historial de ingresos",
  "reports.revenueEmpty.body": "Envía y cobra tus primeras facturas y tu tendencia de seis meses aparecerá aquí.",
  "reports.legend.invoiced": "Facturado",
  "reports.legend.collected": "Cobrado",
  "reports.bidding": "Licitaciones",
  "reports.summary.winRate": "Tasa de éxito",
  "reports.summary.winRate.note": "Todas las cotizaciones a la fecha, aceptadas o cerradas",
  "reports.definitions":
    "Facturado cuenta las aplicaciones de pago G702/G703 y las facturas una vez enviadas — se excluyen borradores y cotizaciones sin facturar. Las cotizaciones solo cuentan para la tasa de éxito.",
};
